import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';
import { parseUrl, stringifyUrl } from 'query-string';
import axios from 'axios';
@Component
export class SideCart extends ThemeComponent {
    @Ref() outer!: HTMLElement;
    @Ref() updatingCart!: HTMLElement;
    @Ref() closeButton!: HTMLButtonElement[];
    @Ref() cartQuantity!: HTMLElement;
    @Ref({ required: false }) note!: HTMLElement[];

    private previouslyFocusedElement: HTMLElement | null = null;

    private scrollPosition: number = 0;

    init() {

        // Give initial value for scrollPosition
        this.scrollPosition = window.pageYOffset;

        // Listen for outer side cart click
        this.listenForOuterClick();

        // Listen for cart item added
        this.listenForCartChanged();

        // Listen for cart item added WHEN ADDED FROM LIGHT DARK TEXT
        this.listenForCartChangedLightDarkText();

        // Listen for close button click
        this.listenForCloseButtonClick();

        // Listen for changes to the cart note
        for (const [i, note] of this.note.entries()) {
            this.$listen(note, 'keyup', () => {
                const noteElement = note as HTMLTextAreaElement;
                cart.updateNote(noteElement.value).catch((event: unknown) => {
                    if (event instanceof Error) {
                        this.$log.error(event.message);
                    }
                });
            });
        }

        this.trapFocus();
        this.listenForEscapeKey();
    }

    public setLoading(loading: boolean = false) {
        if (loading) {
            this.updatingCart.classList.add('loading-spinner--active');
        } else {
            this.updatingCart.classList.remove('loading-spinner--active');
        }
    }

    private listenForOuterClick() {
        this.$listen(this.outer, 'click', (e) => {
            this.close();
        });
    }

    private listenForCartChanged() {
        document.addEventListener('cartChanged', async(e: Event) => {
            this.cartQuantityUpdate();

            // Set loading
            this.setLoading(true);

            // Build URL and get latest side cart markup
            const { url, query } = parseUrl(window.location.href, { decode: false });
            const newUrl = stringifyUrl({
                url: url,
                query: {
                    ...query,
                    sections: 'side-cart',
                }
            }, { arrayFormat: 'none', encode: false });
            const res = await axios.get(newUrl);

            // Prepare correct markup
            const newSideCart = document.createElement('div');
            newSideCart.innerHTML = res.data['side-cart'];
            const newSideCartMain = newSideCart.querySelector('.side-cart__main');
            const currentSideCartMain = this.querySelector('.side-cart__main');
            if (newSideCartMain && currentSideCartMain) {
                currentSideCartMain.outerHTML = newSideCartMain.outerHTML;
            }

            // Show side cart
            this.open();

            // Set loading
            this.setLoading(false);

            // Add event listeners for new close buttons
            this.listenForNewCloseButtonClicks();
        });
    }

    private listenForCartChangedLightDarkText() {
        document.addEventListener('cartChangedLightDarkText', async(e: Event) => {
            this.cartQuantityUpdate();

            // Set loading
            this.setLoading(true);

            // Build URL and get latest side cart markup
            const { url, query } = parseUrl(window.location.href, { decode: false });
            const newUrl = stringifyUrl({
                url: url,
                query: {
                    ...query,
                    sections: 'side-cart',
                }
            }, { arrayFormat: 'none', encode: false });
            const res = await axios.get(newUrl);

            // Prepare correct markup
            const newSideCart = document.createElement('div');
            newSideCart.innerHTML = res.data['side-cart'];
            const newSideCartMain = newSideCart.querySelector('.side-cart__main');
            const currentSideCartMain = this.querySelector('.side-cart__main');
            if (newSideCartMain && currentSideCartMain) {
                currentSideCartMain.outerHTML = newSideCartMain.outerHTML;
            }

            // Set loading
            this.setLoading(false);

            // Add event listeners for new close buttons
            this.listenForNewCloseButtonClicks();
        });
    }

    private listenForCloseButtonClick() {
        for (const button of this.closeButton) {
            this.$listen(button, 'click', (e) => {
                this.close();
            });
        }
    }

    private listenForNewCloseButtonClicks() {
        const closeButtons = this.querySelectorAll('[data-ref="closeButton"]');
        for (const closeButton of closeButtons) {
            const currentCloseButton = closeButton as HTMLButtonElement;
            if (!this.closeButton.includes(currentCloseButton)) {
                this.$listen(currentCloseButton, 'click', (e) => {
                    this.close();
                });
            }
        }
    }

    private cartQuantityUpdate() {
        const count = cart.getCartItemCount();
        this.cartQuantity.textContent = count.toString();
    }

    public open() {
        this.classList.add('side-cart--active');

        // Disable body scrolling
        document.body.classList.add('overlay-active');

        // Store previously focused element and move focus inside cart
        this.previouslyFocusedElement = document.activeElement as HTMLElement;

        // Use setTimeout to ensure DOM is ready after cart opens
        setTimeout(() => {
            const focusable = this.getFocusableElements();
            if (focusable.length > 0) {
                (focusable[0] as HTMLElement).focus();
            }
        }, 100);
    }

    public close() {
        this.classList.remove('side-cart--active');

        // Enable body scrolling
        document.body.classList.remove('overlay-active');


        // restore focus to previously focused element if possible
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }
    }

    private _onOpenChange = new SimpleEvent<string>();
    public get onOpenChange() {
        return this._onOpenChange.asEvent();
    }

    /**
	 * Open Side Cart.
	 */
    public openCart() {
        this._onOpenChange.dispatch('');
        // store previously focused element and move focus inside cart
        this.previouslyFocusedElement = document.activeElement as HTMLElement;
        const focusable = this.getFocusableElements();
        if (focusable.length > 0) {
            (focusable[0] as HTMLElement).focus();
        }
    }
    private closeState: string = '';
    private _onCloseChange = new SimpleEvent<string>();

    public get onCloseChange() {
        return this._onCloseChange.asEvent();
    }

    public changeClose(close: string) {
        this.closeState = close;

        // Use the private backing field to dispatch
        this._onCloseChange.dispatch(close);
    }

    listenForEscapeKey() {
        this.$listen(window, 'keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (this.classList.contains('side-cart--active')) {
                    event.preventDefault();
                    this.close();
                    this._onCloseChange.dispatch('close');
                }
            }
        });
    }

    trapFocus() {
        this.$listen(this, 'keydown', (event: KeyboardEvent) => {
            if (event.key !== 'Tab') {
                return;
            }
            if (!this.classList.contains('side-cart--active')) {
                return;
            }
            const focusable = this.getFocusableElements();
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusable[0] as HTMLElement;
            const last = focusable[focusable.length - 1] as HTMLElement;
            const active = document.activeElement as HTMLElement | null;

            if (event.shiftKey) {
                if (active === first || !this.contains(active)) {
                    event.preventDefault();
                    last.focus();
                }
            } else {
                if (active === last || !this.contains(active)) {
                    event.preventDefault();
                    first.focus();
                }
            }
        });
    }

    private getFocusableElements(): HTMLElement[] {
        const selectors = [
            'a[href]',
            'area[href]',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            'iframe',
            'object',
            'embed',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable="true"]'
        ].join(',');

        const nodes = Array.from(this.querySelectorAll(selectors)) as HTMLElement[];
        // Filter out elements that are not visible
        return nodes.filter((el) => {
            const style = window.getComputedStyle(el);
            const rect = el.getBoundingClientRect();
            return style.visibility !== 'hidden' && style.display !== 'none' && rect.width > 0 && rect.height > 0;
        });
    }
}
